"use client";

import { useEffect, useState, useCallback } from "react";
import { Bell, CheckCircle, RotateCcw, Info, X, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { fetchWithAuth } from "@/lib/api";
import { mapDashboardPath } from "@/lib/routes";
import { Button } from "@/components/ui/button";

type NotificationType = "review_submitted" | "recheck" | "archived" | "approved" | "info";

type Notification = {
  id: string;
  admin_id: string;
  post_id: string | null;
  type: NotificationType;
  message: string;
  is_read: boolean;
  created_at: string;
};

export function NotificationBell() {
  const { profile, previewMode } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const postsBase = mapDashboardPath("/admin/dashboard/posts", profile?.role);
  const reviewBase = mapDashboardPath("/admin/dashboard/blog-review", profile?.role);

  const canFetch = Boolean(profile) && !previewMode;

  const fetchNotifications = useCallback(async () => {
    if (!canFetch) {
      setNotifications([]);
      return;
    }

    try {
      const response = await fetchWithAuth("/api/notifications");
      if (response.status === 401) {
        setNotifications([]);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.data ?? []);
      }
    } catch {
      // silently fail
    }
  }, [canFetch]);

  useEffect(() => {
    if (!canFetch) {
      setNotifications([]);
      setOpen(false);
      return;
    }

    void fetchNotifications();
    const interval = setInterval(() => void fetchNotifications(), 30_000);
    return () => clearInterval(interval);
  }, [canFetch, fetchNotifications]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAllRead = async () => {
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length === 0) return;

    setLoading(true);
    try {
      await fetchWithAuth("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: unreadIds }),
      });
      setNotifications((prev) =>
        prev.map((n) => (unreadIds.includes(n.id) ? { ...n, is_read: true } : n)),
      );
    } catch {
      // silently fail
    }
    setLoading(false);
  };

  const typeConfig: Record<
    NotificationType,
    { icon: typeof Bell; color: string; bg: string }
  > = {
    review_submitted: { icon: FileText, color: "text-violet-400", bg: "bg-violet-500/10" },
    recheck: { icon: RotateCcw, color: "text-amber-400", bg: "bg-amber-500/10" },
    archived: { icon: RotateCcw, color: "text-amber-400", bg: "bg-amber-500/10" },
    approved: { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    info: { icon: Info, color: "text-blue-400", bg: "bg-blue-500/10" },
  };

  const actionLink = (notification: Notification) => {
    if (!notification.post_id) return null;

    if (notification.type === "review_submitted") {
      return (
        <Link
          to={reviewBase}
          className="text-[10px] font-medium text-violet-400 hover:text-violet-300"
          onClick={() => setOpen(false)}
        >
          Review post →
        </Link>
      );
    }

    if (notification.type === "recheck" || notification.type === "archived") {
      return (
        <Link
          to={`${postsBase}/${notification.post_id}/edit`}
          className="text-[10px] font-medium text-amber-400 hover:text-amber-300"
          onClick={() => setOpen(false)}
        >
          Edit & resubmit →
        </Link>
      );
    }

    return null;
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-slate-900/50 text-slate-400 transition hover:bg-slate-800 hover:text-white"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed inset-x-3 top-20 z-50 overflow-hidden rounded-2xl border border-white/10 bg-[#0a0b10] shadow-2xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-12 sm:w-96">
            <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
              <h3 className="text-sm font-semibold text-white">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    disabled={loading}
                    className="text-xs text-slate-400 hover:text-white"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-full p-1 text-slate-400 hover:bg-white/10 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="max-h-[min(70vh,24rem)] overflow-y-auto sm:max-h-80">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <Bell className="h-8 w-8 text-slate-600" />
                  <p className="mt-2 text-sm text-slate-500">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const config = typeConfig[notification.type] ?? typeConfig.info;
                  const Icon = config.icon;
                  return (
                    <div
                      key={notification.id}
                      className={`flex gap-3 border-b border-white/5 px-4 py-3 ${
                        notification.is_read ? "opacity-60" : "bg-white/[0.02]"
                      }`}
                    >
                      <div
                        className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${config.bg}`}
                      >
                        <Icon className={`h-4 w-4 ${config.color}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="break-words text-sm text-zinc-200">{notification.message}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="text-[10px] text-zinc-500">
                            {new Date(notification.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                          {actionLink(notification)}
                        </div>
                      </div>
                      {!notification.is_read && (
                        <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-400" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
